<template>
  <ContentSection spacing="none" class="pb-16 transition-[height] sm:pb-28" :class="{ 'pt-12': index === 0 }">
    <Container indent="lg-but-on-mobile-default">
      <div class="mb-6 space-y-2 sm:mb-9">
        <!-- HEADER -->
        <h2
          v-if="data.heading"
          v-animate-in-from-bottom
          class="typo-heading-lg sm:typo-heading-xl"
          v-html="data.heading"
        />
        <!-- TEXT -->
        <div
          v-if="data.text"
          v-animate-in-from-bottom
          class="typo-body-xl sm:typo-body-2xl"
          v-html="data.text"
        />
      </div>

      <!-- FILTER -->
      <OverviewFilter
        v-if="data.filter_show"
        v-model="filter"
        class="mb-8 sm:mb-16"
        :categories="props.data.team_categories"
        @category="filter.category = $event"
      />
    </Container>

    <Container>
      <!-- TEAM MEMBERS -->
      <TransitionGroup
        tag="ul"
        name="list"
        class="grid grid-cols-1 gap-5 sm:grid-cols-[repeat(auto-fill,_minmax(17rem,_1fr))] sm:gap-6"
      >
        <li v-for="member in entries" :key="member?.id">
          <CardTeamMember :data="member" />
        </li>
      </TransitionGroup>

      <!-- PAGINATION -->
      <Pagination
        v-if="totalPages > 1"
        :page="page"
        :total-pages="totalPages"
        @paginate="fetchEntries($event)"
      />

      <!-- NO RESULTS -->
      <Transition name="fade-in">
        <NoEntriesFound v-if="!entries?.length" />
      </Transition>
    </Container>
  </ContentSection>
</template>

<script setup lang="ts">
import type { TeamMemberFragment, ComponentsMainOverviewTeamFragment } from '#gql'

const props = defineProps<{
  data: ComponentsMainOverviewTeamFragment
  index?: number
}>()

const route = useRoute()
const router = useRouter()
const { locale } = useI18n()

// Filter
export type FilterType = {
  category: string | null

}
const filter = ref<FilterType>({
  category: route.query.category ? (route.query.category as string) : null,

})

// Pagination
const limit = 20
const page = ref(1)
const hasMorePages = ref<boolean>(props.data.has_more_pages ?? false)
const totalPages = ref(parseInt(props.data.total || 1))

// Entries
const entries = ref<ComponentsMainOverviewTeamFragment['entries']>(props.data.entries)

// If there is a filter set as url query param, fetch entries with that filter
if (route.query.category) {
  const payload = await useAsyncGql({
    operation: 'TeamMembers',
    variables: {
      sort: 'title asc',
      limit: limit,
      page: page.value,
      filter: {
        site: locale.value,
        category: {
          contains: route.query.category,
          not: null,
        },
      },
    },
  })

  if (payload.data.value?.entries?.data) {
    entries.value = payload.data.value.entries.data
    hasMorePages.value = payload.data.value.entries.has_more_pages
    totalPages.value = payload.data.value.entries.total
  }
}

// Load Entries on filter change
const GqlInstance = useGql()
async function fetchEntries(desiredPage: number = page.value) {
  if (desiredPage !== undefined) {
    page.value = desiredPage
  }

  const payload = await GqlInstance('TeamMembers', {
    sort: 'title asc',
    limit: limit,
    page: page.value,
    filter: {
      site: locale.value,
      category: {
        contains: filter.value.category || null,
        not: null,
      },
    },
  })

  if (!payload?.entries?.data) {
    console.error('Team Members could not be fetched')
    return
  }

  hasMorePages.value = payload.entries.has_more_pages
  totalPages.value = payload.entries.last_page

  entries.value = payload.entries.data as TeamMemberFragment[]
}

// Saves the filter in the URL
function saveFilterInUrl() {
  router.push({
    query: {
      category: filter.value.category,
    },
  })
}

// Watch for filter changes
watch(
  () => filter.value,
  () => {
    // saveFilterInUrl()
    // Reset page to 1
    page.value = 1
    fetchEntries()
  },
  { deep: true },
)
</script>
